import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  Text,
  Box,
  Flex,
  SimpleGrid,
  VStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED_LANGUAGES } from "../i18n/config";
import CountryFlag from "./ui/CountryFlag";

export default function LanguagePickerModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const isNarrow = useBreakpointValue({ base: true, md: false }, { ssr: false });

  const panelBg = useColorModeValue("gray.900", "gray.900");
  const borderColor = useColorModeValue("whiteAlpha.200", "whiteAlpha.200");
  const titleColor = "white";
  const mutedColor = "whiteAlpha.700";
  const rowBg = useColorModeValue("whiteAlpha.100", "whiteAlpha.100");
  const rowHoverBg = useColorModeValue("whiteAlpha.160", "whiteAlpha.160");
  const rowBorder = useColorModeValue("whiteAlpha.150", "whiteAlpha.150");

  const currentCode = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const currentMeta = SUPPORTED_LANGUAGES.find((l) => l.code === currentCode) ?? SUPPORTED_LANGUAGES[0];

  const handlePick = (code) => {
    void i18n.changeLanguage(code);
    onClose();
  };

  const body = (
    <VStack spacing={4} align="stretch" pt={1}>
      <Flex align="center" justify="center" gap={2} flexWrap="nowrap" w="100%" overflow="hidden">
        <CountryFlag country={currentMeta.flagCountry} size={16} borderRadius="3px" flexShrink={0} />
        <Text
          as="span"
          fontSize="sm"
          color={mutedColor}
          lineHeight="1"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          {t("languageModal.current", { name: currentMeta.native })}
        </Text>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} w="100%">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const active = currentCode === lang.code;
          return (
            <Box
              key={lang.code}
              as="button"
              type="button"
              w="100%"
              borderRadius="xl"
              px={3}
              py={3}
              borderWidth="1px"
              borderColor={active ? "brand.400" : rowBorder}
              bg={active ? "whiteAlpha.200" : rowBg}
              transition="background 0.15s, border-color 0.15s"
              _hover={{ bg: rowHoverBg, borderColor: "brand.300" }}
              onClick={() => handlePick(lang.code)}
            >
              <Flex direction="column" align="center" justify="center" gap={2}>
                <CountryFlag country={lang.flagCountry} size={20} borderRadius="3px" flexShrink={0} />
                <Text
                  fontWeight="semibold"
                  color={titleColor}
                  fontSize="sm"
                  letterSpacing="-0.01em"
                  lineHeight="1.2"
                  textAlign="center"
                  whiteSpace="nowrap"
                >
                  {lang.native}
                </Text>
              </Flex>
            </Box>
          );
        })}
      </SimpleGrid>
    </VStack>
  );

  if (isNarrow) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} size="full">
        <DrawerOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
        <DrawerContent bg={panelBg} borderTopRadius="2xl" borderTopWidth="1px" borderColor={borderColor}>
          <DrawerCloseButton color={titleColor} />
          <DrawerBody pb={8} pt={10} px={5}>
            <Text fontSize="xl" fontWeight="bold" color={titleColor} textAlign="center" mb={1}>
              {t("languageModal.title")}
            </Text>
            {body}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent bg={panelBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} mx={4}>
        <ModalHeader color={titleColor} textAlign="center" fontWeight="bold" fontSize="xl" pt={8} pb={0} px={8}>
          {t("languageModal.title")}
        </ModalHeader>
        <ModalCloseButton color={titleColor} />
        <ModalBody pb={8} pt={6} px={8}>
          {body}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
